import { readValue, asArray, configurationErrorMessage } from "../utils/storefrontApi";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddOutlined,
  DeleteOutline,
  HistoryOutlined,
  RefreshOutlined,
  RestoreOutlined,
  SaveOutlined,
  ScienceOutlined,
  UndoOutlined,
} from "@mui/icons-material";
import axiosInstanceSynonyms from "../axiosInstanceSynonyms";
import TenantAutocomplete from "./TenantAutocomplete";

const MAX_RULES = 250;
const MAX_RULE_ID_LENGTH = 80;
const MAX_PHRASE_LENGTH = 80;
const MAX_PHRASE_TOKENS = 8;
const MAX_TERMS = 10;
const splitPhrases = (value) => value.split(/[\n,]/).map((phrase) => phrase.trim()).filter(Boolean);

const createRule = (type = "Equivalent", index = 0) => ({
  id: `rule-${Date.now()}-${index + 1}`,
  type,
  termsText: "",
  source: "",
  targetsText: "",
  enabled: true,
});

const normalizeRule = (rule) => ({
  id: readValue(rule, "id", "Id") || "",
  type: readValue(rule, "type", "Type") || "Equivalent",
  termsText: asArray(readValue(rule, "terms", "Terms")).join("\n"),
  source: readValue(rule, "source", "Source") || "",
  targetsText: asArray(readValue(rule, "targets", "Targets")).join("\n"),
  enabled: readValue(rule, "enabled", "Enabled") !== false,
});

const serializeRule = (rule) => rule.type === "Equivalent"
  ? { id: rule.id.trim(), type: "Equivalent", terms: splitPhrases(rule.termsText), source: null, targets: [], enabled: rule.enabled }
  : { id: rule.id.trim(), type: "OneWay", terms: [], source: rule.source.trim(), targets: splitPhrases(rule.targetsText), enabled: rule.enabled };

const validatePhrases = (phrases, minimum, label) => {
  if (phrases.length < minimum || phrases.length > MAX_TERMS) return `${label} require between ${minimum} and ${MAX_TERMS} distinct phrases.`;
  if (new Set(phrases.map((phrase) => phrase.toLocaleLowerCase())).size !== phrases.length) return "Phrases must be unique.";
  if (phrases.some((phrase) => phrase.length > MAX_PHRASE_LENGTH)) return `Each phrase must be ${MAX_PHRASE_LENGTH} characters or fewer.`;
  if (phrases.some((phrase) => phrase.split(/\s+/).length > MAX_PHRASE_TOKENS)) return `Each phrase must contain ${MAX_PHRASE_TOKENS} words or fewer.`;
  return null;
};

const validateRules = (rules) => {
  const ids = new Set();
  return rules.map((rule) => {
    const errors = {};
    const id = rule.id.trim();
    if (!id) errors.id = "Rule ID is required.";
    else if (id.length > MAX_RULE_ID_LENGTH) errors.id = `Rule ID cannot exceed ${MAX_RULE_ID_LENGTH} characters.`;
    else if (ids.has(id)) errors.id = "Rule ID must be unique.";
    ids.add(id);
    if (rule.type === "Equivalent") errors.phrases = validatePhrases(splitPhrases(rule.termsText), 2, "Equivalent rules") || undefined;
    if (rule.type === "OneWay") {
      const source = rule.source.trim();
      if (!source) errors.source = "Source phrase is required.";
      else if (source.length > MAX_PHRASE_LENGTH) errors.source = `Source phrase must be ${MAX_PHRASE_LENGTH} characters or fewer.`;
      else if (source.split(/\s+/).length > MAX_PHRASE_TOKENS) errors.source = `Source phrase must contain ${MAX_PHRASE_TOKENS} words or fewer.`;
      const targets = splitPhrases(rule.targetsText);
      errors.phrases = validatePhrases(targets, 1, "One-way rules") || undefined;
      if (!errors.phrases && targets.some((target) => target.toLocaleLowerCase() === source.toLocaleLowerCase())) errors.phrases = "A source cannot target itself.";
    }
    Object.keys(errors).forEach((key) => errors[key] === undefined && delete errors[key]);
    return errors;
  });
};

const getErrorMessage = configurationErrorMessage;

const RuleCard = ({ rule, index, errors, onChange, onRemove }) => (
  <Paper data-testid={`synonym-rule-${index}`} elevation={0} sx={{ p: 1.6, borderRadius: "16px", border: `1px solid ${Object.keys(errors).length ? "rgba(239,68,68,0.35)" : "rgba(15,23,42,0.08)"}` }}>
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Chip size="small" label={`Rule ${index + 1}`} />
        <Chip size="small" variant="outlined" color={rule.type === "Equivalent" ? "warning" : "secondary"} label={rule.type === "Equivalent" ? "Equivalent" : "One-way"} />
      </Stack>
      <Tooltip title="Remove rule"><IconButton aria-label={`Remove synonym rule ${index + 1}`} size="small" onClick={onRemove}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
    </Stack>
    <Stack spacing={1.25} sx={{ mt: 1.4 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField fullWidth size="small" label="Rule ID" value={rule.id} onChange={(event) => onChange("id", event.target.value)} inputProps={{ maxLength: MAX_RULE_ID_LENGTH }} error={Boolean(errors.id)} helperText={errors.id || "Stable identity used in diagnostics."} />
        <TextField select size="small" label="Rule type" value={rule.type} onChange={(event) => onChange("type", event.target.value)} SelectProps={{ native: true }} sx={{ minWidth: 150 }}>
          <option value="Equivalent">Equivalent</option>
          <option value="OneWay">One-way</option>
        </TextField>
      </Stack>
      {rule.type === "Equivalent" ? (
        <TextField multiline minRows={2} label="Equivalent terms" value={rule.termsText} onChange={(event) => onChange("termsText", event.target.value)} error={Boolean(errors.phrases)} helperText={errors.phrases || "One phrase per line; expansion works in every direction."} />
      ) : (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField fullWidth label="Source phrase" value={rule.source} onChange={(event) => onChange("source", event.target.value)} error={Boolean(errors.source)} helperText={errors.source || "Only this phrase triggers the rule."} />
          <TextField fullWidth multiline minRows={2} label="Target phrases" value={rule.targetsText} onChange={(event) => onChange("targetsText", event.target.value)} error={Boolean(errors.phrases)} helperText={errors.phrases || "One target per line; reverse expansion is disabled."} />
        </Stack>
      )}
      <FormControlLabel control={<Switch size="small" color="warning" checked={rule.enabled} onChange={(_, checked) => onChange("enabled", checked)} />} label={rule.enabled ? "Enabled" : "Disabled"} />
    </Stack>
  </Paper>
);

const StorefrontSynonymsLab = ({ tenants = [], selectedTenant, setSelectedTenant }) => {
  const [version, setVersion] = useState(0);
  const [rules, setRules] = useState([]);
  const [savedRules, setSavedRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [previewQuery, setPreviewQuery] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [rollbackVersion, setRollbackVersion] = useState("");
  const [rollingBack, setRollingBack] = useState(false);

  const tenant = useMemo(() => tenants.find((item) => item.id === selectedTenant) || null, [selectedTenant, tenants]);
  const endpoint = selectedTenant ? `/api/tenant/${encodeURIComponent(selectedTenant)}/storefront-synonyms` : "";

  const applyConfiguration = useCallback((data, fallbackVersion) => {
    const nextRules = asArray(readValue(data, "rules", "Rules")).map(normalizeRule);
    setVersion(Number(readValue(data, "version", "Version") || fallbackVersion));
    setRules(nextRules);
    setSavedRules(nextRules);
  }, []);

  useEffect(() => {
    setFeedback(null);
    setPreview(null);
  }, [selectedTenant]);

  useEffect(() => {
    if (!endpoint) {
      setRules([]);
      setSavedRules([]);
      setHistory([]);
      return undefined;
    }
    let ignore = false;
    const load = async () => {
      setLoading(true);
      setPreview(null);
      try {
        const [configurationResponse, historyResponse] = await Promise.all([
          axiosInstanceSynonyms.get(endpoint),
          axiosInstanceSynonyms.get(`${endpoint}/history`, { params: { limit: 20 } }),
        ]);
        if (ignore) return;
        applyConfiguration(configurationResponse.data, 0);
        setHistory(asArray(readValue(historyResponse.data, "items", "Items")));
        setRollbackVersion("");
      } catch (error) {
        if (!ignore) setFeedback({ severity: "error", message: getErrorMessage(error, "Failed to load storefront synonyms.") });
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    load();
    return () => { ignore = true; };
  }, [endpoint, refreshKey, applyConfiguration]);

  const errors = useMemo(() => validateRules(rules), [rules]);
  const hasErrors = rules.length > MAX_RULES || errors.some((item) => Object.keys(item).length);
  const dirty = JSON.stringify(rules) !== JSON.stringify(savedRules);
  const enabledCount = rules.filter((rule) => rule.enabled).length;

  const refreshHistory = async () => {
    const { data } = await axiosInstanceSynonyms.get(`${endpoint}/history`, { params: { limit: 20 } });
    setHistory(asArray(readValue(data, "items", "Items")));
  };

  const updateRule = (index, field, value) => {
    setRules((current) => current.map((rule, ruleIndex) => ruleIndex === index ? { ...rule, [field]: value } : rule));
    setFeedback(null);
  };

  const save = async () => {
    if (!dirty || hasErrors) return;
    setSaving(true);
    setFeedback(null);
    try {
      const { data } = await axiosInstanceSynonyms.put(endpoint, { expectedVersion: version, rules: rules.map(serializeRule) });
      applyConfiguration(data, version + 1);
      setFeedback({ severity: "success", message: "Storefront synonyms saved as a new version." });
      refreshHistory().catch(() => undefined);
    } catch (error) {
      setFeedback({ severity: "error", message: getErrorMessage(error, "Failed to save storefront synonyms.") });
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (!previewQuery.trim() || hasErrors) return;
    setPreviewing(true);
    setFeedback(null);
    try {
      const { data } = await axiosInstanceSynonyms.post(`${endpoint}/preview`, {
        query: previewQuery.trim(),
        rules: rules.map(serializeRule),
        limit: 20,
      });
      setPreview(data);
    } catch (error) {
      setPreview(null);
      setFeedback({ severity: "error", message: getErrorMessage(error, "Failed to preview storefront synonyms.") });
    } finally {
      setPreviewing(false);
    }
  };

  const rollback = async () => {
    if (rollbackVersion === "") return;
    setRollingBack(true);
    setFeedback(null);
    try {
      const { data } = await axiosInstanceSynonyms.post(`${endpoint}/rollback`, { expectedVersion: version, targetVersion: Number(rollbackVersion) });
      applyConfiguration(data, version + 1);
      setRollbackVersion("");
      setFeedback({ severity: "success", message: `Rolled back version ${rollbackVersion} as a new active version.` });
      refreshHistory().catch(() => undefined);
    } catch (error) {
      setFeedback({ severity: "error", message: getErrorMessage(error, "Failed to roll back storefront synonyms.") });
    } finally {
      setRollingBack(false);
    }
  };

  const previewResults = asArray(readValue(preview, "results", "Results"));
  const synonymDiagnostics = readValue(preview, "synonyms", "Synonyms");

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Paper elevation={0} sx={{ p: 2, borderRadius: "18px", border: "1px solid rgba(15,23,42,0.08)" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
          <TenantAutocomplete tenants={tenants} value={tenant} onChange={(_, value) => setSelectedTenant(value?.id || "")} label="Tenant" placeholder="Choose a catalogue" fullWidth size="small" />
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: { md: 320 } }}>
            <Chip label={`Version ${version}`} color="warning" variant="outlined" />
            <Chip label={`${enabledCount} enabled · ${rules.length} total`} />
            <IconButton aria-label="Refresh synonyms" disabled={!selectedTenant || loading} onClick={() => setRefreshKey((value) => value + 1)}><RefreshOutlined /></IconButton>
          </Stack>
        </Stack>
      </Paper>

      {!selectedTenant ? <Alert severity="info">Select a tenant to manage its storefront synonym configuration.</Alert> : null}
      {loading ? <Stack alignItems="center" spacing={1} sx={{ py: 8 }}><CircularProgress sx={{ color: "#f97316" }} /><Typography color="text.secondary">Loading synonym configuration…</Typography></Stack> : null}
      {feedback ? <Alert severity={feedback.severity}>{feedback.message}</Alert> : null}

      {selectedTenant && !loading ? (
        <>
          <Alert severity="info" sx={{ borderRadius: "14px" }}>Preview uses the complete unsaved rule set without persisting it. Saving atomically replaces the active configuration and increments its version.</Alert>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 7fr) minmax(340px, 5fr)" }, gap: 2, alignItems: "start" }}>
            <Stack spacing={1.5}>
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)" }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "center" }}>
                  <Box><Typography sx={{ fontWeight: 850 }}>Synonym rules</Typography><Typography variant="caption" color="text.secondary">Equivalent rules expand both ways; one-way rules expand only source → targets.</Typography></Box>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Button startIcon={<AddOutlined />} disabled={rules.length >= MAX_RULES} onClick={() => setRules((current) => [...current, createRule("Equivalent", current.length)])} sx={{ textTransform: "none" }}>Equivalent</Button>
                    <Button startIcon={<AddOutlined />} disabled={rules.length >= MAX_RULES} onClick={() => setRules((current) => [...current, createRule("OneWay", current.length)])} sx={{ textTransform: "none" }}>One-way</Button>
                    <Button startIcon={<UndoOutlined />} disabled={!dirty || saving} onClick={() => setRules(savedRules.map((rule) => ({ ...rule })))} sx={{ textTransform: "none" }}>Discard</Button>
                    <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />} disabled={!dirty || hasErrors || saving} onClick={save} sx={{ textTransform: "none", backgroundColor: "#f97316", boxShadow: "none" }}>{saving ? "Saving…" : "Save new version"}</Button>
                  </Stack>
                </Stack>
              </Paper>
              {rules.map((rule, index) => <RuleCard key={`${rule.id}-${index}`} rule={rule} index={index} errors={errors[index]} onChange={(field, value) => updateRule(index, field, value)} onRemove={() => setRules((current) => current.filter((_, ruleIndex) => ruleIndex !== index))} />)}
              {!rules.length ? <Paper elevation={0} sx={{ py: 6, textAlign: "center", border: "1px dashed rgba(15,23,42,0.15)", borderRadius: "16px" }}><Typography color="text.secondary">No synonym rules. Add an equivalent or one-way rule to begin.</Typography></Paper> : null}
            </Stack>

            <Stack spacing={1.5}>
              <Paper elevation={0} sx={{ p: 1.7, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)" }}>
                <Stack direction="row" spacing={0.75} alignItems="center"><ScienceOutlined sx={{ color: "#f97316" }} /><Typography sx={{ fontWeight: 850 }}>Transient preview</Typography></Stack>
                <Typography variant="caption" color="text.secondary">Runs lexical-v3 with the rules currently in the editor. Nothing is saved.</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                  <TextField fullWidth size="small" label="Preview query" value={previewQuery} onChange={(event) => setPreviewQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") runPreview(); }} />
                  <Button variant="contained" disabled={!previewQuery.trim() || hasErrors || previewing} onClick={runPreview} sx={{ textTransform: "none", backgroundColor: "#f97316", boxShadow: "none" }}>{previewing ? <CircularProgress size={18} color="inherit" /> : "Preview"}</Button>
                </Stack>
                {preview ? (
                  <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={`${Number(readValue(preview, "total", "Total") || 0)} results`} />
                      <Chip size="small" label={readValue(preview, "retrievalStage", "RetrievalStage") || "primary"} variant="outlined" />
                      <Chip size="small" label={readValue(synonymDiagnostics, "expansionApplied", "ExpansionApplied") ? "Expansion applied" : "No expansion"} color={readValue(synonymDiagnostics, "expansionApplied", "ExpansionApplied") ? "success" : "default"} />
                    </Stack>
                    {asArray(readValue(synonymDiagnostics, "rewrites", "Rewrites")).length ? <Typography variant="caption" color="text.secondary">Rewrites: {asArray(readValue(synonymDiagnostics, "rewrites", "Rewrites")).join(" · ")}</Typography> : null}
                    {previewResults.slice(0, 10).map((result) => {
                      const product = readValue(result, "product", "Product") || {};
                      return <Box key={readValue(product, "id", "Id")} sx={{ display: "grid", gridTemplateColumns: "34px minmax(0,1fr) auto", gap: 0.75, p: 0.8, borderRadius: "10px", backgroundColor: "#f8fafc" }}><Typography variant="caption" fontWeight={900} color="#ea580c">#{readValue(result, "position", "Position")}</Typography><Typography variant="caption" fontWeight={750}>{readValue(product, "title", "Title") || readValue(product, "id", "Id")}</Typography><Typography variant="caption" fontFamily="monospace">{Number(readValue(result, "score", "Score") || 0).toFixed(2)}</Typography></Box>;
                    })}
                  </Stack>
                ) : null}
              </Paper>

              <Paper elevation={0} sx={{ p: 1.7, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)" }}>
                <Stack direction="row" spacing={0.75} alignItems="center"><HistoryOutlined sx={{ color: "#64748b" }} /><Typography sx={{ fontWeight: 850 }}>Version history</Typography></Stack>
                <Typography variant="caption" color="text.secondary">Rollback copies an earlier rule set into a new active version; history remains immutable.</Typography>
                <TextField select fullWidth size="small" label="Rollback target" value={rollbackVersion} onChange={(event) => setRollbackVersion(event.target.value)} SelectProps={{ native: true }} sx={{ mt: 1.25 }}>
                  <option value="">Select an earlier version</option>
                  {history.filter((item) => Number(readValue(item, "version", "Version")) !== version).map((item) => <option key={readValue(item, "version", "Version")} value={readValue(item, "version", "Version")}>Version {readValue(item, "version", "Version")} · {readValue(item, "changeType", "ChangeType") || "change"}</option>)}
                </TextField>
                <Button fullWidth variant="outlined" color="warning" startIcon={rollingBack ? <CircularProgress size={16} /> : <RestoreOutlined />} disabled={rollbackVersion === "" || rollingBack} onClick={rollback} sx={{ mt: 1, textTransform: "none" }}>{rollingBack ? "Rolling back…" : rollbackVersion === "" ? "Choose a version" : `Roll back to version ${rollbackVersion}`}</Button>
                <Stack spacing={0.65} sx={{ mt: 1.25 }}>
                  {history.map((item) => <Box key={`${readValue(item, "tenantVersion", "TenantVersion")}-${readValue(item, "version", "Version")}`} sx={{ p: 0.85, borderRadius: "10px", backgroundColor: "#f8fafc" }}><Stack direction="row" justifyContent="space-between"><Typography variant="caption" fontWeight={800}>Version {readValue(item, "version", "Version")}</Typography><Typography variant="caption" color="text.secondary">{asArray(readValue(item, "rules", "Rules")).length} rules</Typography></Stack><Typography variant="caption" color="text.secondary">{readValue(item, "changeType", "ChangeType") || "Updated"} · {readValue(item, "updatedAtUtc", "UpdatedAtUtc") ? new Date(readValue(item, "updatedAtUtc", "UpdatedAtUtc")).toLocaleString() : "Unknown date"}</Typography></Box>)}
                  {!history.length ? <Typography variant="caption" color="text.secondary">No archived versions are available.</Typography> : null}
                </Stack>
              </Paper>
            </Stack>
          </Box>
        </>
      ) : null}
    </Stack>
  );
};

export { normalizeRule, serializeRule, splitPhrases, validateRules };
export default StorefrontSynonymsLab;
